import { ApiResponse, ApiResponseSchema } from '@/shared/schemas/api-schema'
import { api, ApiError } from '@shared/api/api-factory'
import {
  AuthResponse,
  AuthResponseSchema,
  LoginFormValues,
  PasswordResetFormValues,
  PasswordResetRequestFormValues,
  RegisterFormValues,
  User,
  UserSchema,
} from '@/shared/schemas/auth-schema'
import { z } from 'zod'

const AUTH_BASE_URL = "/auth"

const parseApiResponse = <T>(
  responseData: unknown,
  dataSchema: z.ZodSchema<T>
): ApiResponse<T> => {

  const isApiResponseFormat = typeof responseData === 'object' &&
    responseData !== null &&
    'statusCode' in responseData &&
    'data' in responseData

  if (isApiResponseFormat) {
    const parsedResponse = ApiResponseSchema.safeParse(responseData)

    if (!parsedResponse.success) {
      console.error("API Response Schema Error:", parsedResponse.error)
      throw new ApiError({ statusCode: 500, message: "Invalid API response format" })
    }
  
    if (parsedResponse.data.statusCode >= 400) {
      return {
        ...parsedResponse.data,
        data: undefined
      } as ApiResponse<T>
    }

    const parsedData = dataSchema.safeParse(parsedResponse.data.data)
    if (!parsedData.success) {
      console.error("Data Schema Error:", parsedData.error)
      console.error("Failed to parse data:", parsedResponse.data.data)
      console.error("Schema validation details:", parsedData.error.issues)
      throw new ApiError({ statusCode: 500, message: "Invalid response data format" })
    }

    return { ...parsedResponse.data, data: parsedData.data }
  }
  else {
    const parsedData = dataSchema.safeParse(responseData)

    if (!parsedData.success) {
      console.error("Direct Data Schema Error:", parsedData.error)
      throw new ApiError({
        statusCode: 500,
        message: "Invalid response data format",
        errorCode: "PARSE_ERROR"
      })
    }

    return {
      statusCode: 200,
      message: "Success",
      data: parsedData.data,
      errorCode: null
    }
  }
}

export const authApi = {
  register: async (data: RegisterFormValues): Promise<ApiResponse<AuthResponse>> => {
    const response = await api.post<ApiResponse<AuthResponse>>(`${AUTH_BASE_URL}/register`, data)
    return parseApiResponse(response.data, AuthResponseSchema)
  },

  login: async (data: LoginFormValues): Promise<ApiResponse<AuthResponse>> => {
    const response = await api.post<ApiResponse<AuthResponse>>(
      `${AUTH_BASE_URL}/login`,
      data,
      { headers: { 'X-Skip-Auth': 'true' } }
    )
    return parseApiResponse(response.data, AuthResponseSchema)
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    const response = await api.get<ApiResponse<User>>(`${AUTH_BASE_URL}/me`)
    return parseApiResponse(response.data, UserSchema);
  },

  requestPasswordReset: async (data: PasswordResetRequestFormValues): Promise<void> => {
    await api.post(`${AUTH_BASE_URL}/forgot-password`, data)
  },

  resetPassword: async (data: PasswordResetFormValues): Promise<void> => {
    await api.post(`${AUTH_BASE_URL}/reset-password`, data)
  },

  verifyEmail: async (token: string): Promise<void> => {
    await api.post(`${AUTH_BASE_URL}/verify-email`, { token })
  },

  refreshToken: async (refreshToken: string): Promise<ApiResponse<AuthResponse>> => {
    const response = await api.post<ApiResponse<AuthResponse>>(`${AUTH_BASE_URL}/refresh-token`, { refreshToken })
    const parsed = parseApiResponse(response.data, AuthResponseSchema)
    if (!parsed.data) {
      throw new ApiError(parsed)
    }
    return parsed;
  },
}
